/**
 * @fileoverview Main entry point for the storyteller package
 *
 * @example
 * // Usage:
 * import { start } from './index'
 *
 * function Component() {
 *   // ...
 * }
 */

import axios from 'axios'
import axiosRetry from 'axios-retry'
import { Root, createRoot } from 'react-dom/client'
import { App } from './components/App'

import { MockApp } from './components/MockApp'
import { NoTest } from './components/NoTest'
import { setupLanguages } from './i18n'

/**
 * @function setupAxiosMiddleware
 * @description Sets up the axios middleware
 * @param {string} token - The token to use for authentication
 */
function setupAxiosMiddleware(token: string) {
    axiosRetry(axios, { retries: 3, retryDelay: axiosRetry.exponentialDelay })

    axios.defaults.headers.common.Authorization = `Bearer ${token}`

    axios.interceptors.request.clear()

    axios.interceptors.request.use(
        (config) => {
            if (config.method !== 'post') {
                return config
            }

            if (typeof config.data === 'string') {
                return config
            }
            return config
        },
        (error: unknown) => {
            return Promise.reject(error)
        }
    )
}

/**
 * @function renderScreen
 * @description Renders the screen
 * @param {HTMLElement} element - The element to render the screen in
 * @param {Novoic.Data & { question: Novoic.AnyQuestion; isLegacyBattery: boolean }} data - The data to render the screen with
 * @returns {Root} The root element
 */
export function renderScreen(
    element: HTMLElement,
    {
        question,
        language = 'en_US',
        token,
        isLegacyBattery = false,
    }: Novoic.Data & { question: Novoic.AnyQuestion; isLegacyBattery: boolean }
): Root {
    setupAxiosMiddleware(token)
    setupLanguages(language)

    const root = createRoot(element)
    root.render(
        <MockApp assignmentId="mock" question={question} isLegacyBattery={isLegacyBattery} />
    )

    return root
}

/**
 * @function start
 * @description Starts the storyteller
 * @param {HTMLElement} element - The element to render the storyteller in
 * @param {Novoic.Data} data - The data to render the storyteller with
 * @returns {Root} The root element
 */
export function start(
    element: HTMLElement,
    {
        assignmentId,
        language,
        standalone,
        token,
        onComplete,
        onLanguageChange,
        onExit,
        onTimeout,
        onLoad,
        onProgress,
    }: Novoic.Data
): Root {
    setupAxiosMiddleware(token)
    setupLanguages(language)

    const root = createRoot(element)

    if (!assignmentId) {
        root.render(<NoTest />)
    } else {
        root.render(
            <App
                assignmentId={assignmentId}
                standalone={standalone}
                onProgress={onProgress}
                onLoad={onLoad}
                onComplete={onComplete}
                onTimeout={onTimeout}
                onLanguageChange={onLanguageChange}
                onExit={onExit}
            />
        )
    }

    return root
}
